import React, { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import TextField from "@material-ui/core/TextField";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import IconButton from "@material-ui/core/IconButton";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TablePagination from "@material-ui/core/TablePagination";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import RefreshIcon from "@material-ui/icons/Refresh";
import GetAppIcon from "@material-ui/icons/GetApp";
import PublishIcon from "@material-ui/icons/Publish";

import ConfirmationModal from "../../components/ConfirmationModal";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import { menuPermissionOptions } from "../../layout/MainListItems";

const ALL_SUPERADMIN_STATUSES = "__all_superadmin_statuses__";
const ALL_SUPERADMIN_PERMISSIONS = "__all_superadmin_permissions__";

const useStyles = makeStyles(theme => ({
  headerContent: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    [theme.breakpoints.down("xs")]: {
      alignItems: "stretch",
      flexDirection: "column",
      gap: theme.spacing(1),
    },
  },
  headerButtons: {
    [theme.breakpoints.down("xs")]: {
      display: "grid",
      gridTemplateColumns: "1fr",
      gap: theme.spacing(1),
      marginLeft: 0,
      "& > *": {
        margin: 0,
      },
      "& .MuiButton-root": {
        minHeight: 42,
      },
    },
  },
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "auto",
    margin: 20,
    ...theme.scrollbarStyles,
    [theme.breakpoints.down("xs")]: {
      margin: theme.spacing(1),
      padding: theme.spacing(1),
      border: 0,
      boxShadow: "none",
      backgroundColor: "transparent"
    }
  },
  filters: {
    display: "grid",
    gridTemplateColumns: "minmax(240px, 1.4fr) repeat(3, minmax(150px, 1fr))",
    gap: theme.spacing(1),
    alignItems: "center",
    marginBottom: theme.spacing(1.5),
    "& .MuiButton-root": {
      minHeight: 40
    },
    "& .MuiFormControl-root, & .MuiTextField-root": {
      minWidth: 0
    },
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(2, minmax(220px, 1fr))"
    },
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))"
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr"
    }
  },
  tabBar: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(1.5)
  },
  tableWrapper: {
    overflowX: "auto",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    backgroundColor: theme.palette.background.paper
  },
  table: {
    minWidth: 920
  },
  actionCell: {
    whiteSpace: "nowrap"
  },
  trialForm: {
    display: "grid",
    gap: theme.spacing(1)
  },
  pagination: {
    borderTop: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(1),
    [theme.breakpoints.down("xs")]: {
      "& .MuiTablePagination-toolbar": {
        alignItems: "stretch",
        flexDirection: "column",
        height: "auto",
        gap: theme.spacing(1),
        padding: theme.spacing(1)
      },
      "& .MuiTablePagination-spacer": {
        display: "none"
      }
    }
  },
  accountCard: {
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    backgroundColor: theme.palette.background.paper
  },
  accountHeader: {
    padding: 0,
    [theme.breakpoints.down("xs")]: {
      padding: 0
    }
  },
  accountMeta: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1)
  },
  sectionTitle: {
    margin: theme.spacing(2, 0, 1),
    fontWeight: 600
  },
  emptyText: {
    padding: theme.spacing(1),
    color: theme.palette.text.secondary
  },
  accountControls: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    [theme.breakpoints.down("xs")]: {
      display: "grid",
      gridTemplateColumns: "1fr",
      alignItems: "stretch",
      "& .MuiTextField-root": {
        width: "100%"
      },
      "& .MuiButton-root": {
        minHeight: 42
      }
    }
  },
  menuPermissions: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
    [theme.breakpoints.down("xs")]: {
      display: "grid",
      gridTemplateColumns: "1fr",
      alignItems: "stretch"
    }
  },
  menuPermissionsActions: {
    display: "flex",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    [theme.breakpoints.down("xs")]: {
      display: "grid",
      gridTemplateColumns: "1fr",
      "& .MuiButton-root": {
        minHeight: 42
      }
    }
  },
  relatedCards: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1)
  },
  relatedCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(1.25),
    backgroundColor: theme.palette.background.paper
  },
  relatedCardHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(1)
  },
  relatedTitle: {
    fontWeight: 600,
    lineHeight: 1.25,
    wordBreak: "break-word"
  },
  relatedMeta: {
    color: theme.palette.text.secondary,
    fontSize: 13,
    lineHeight: 1.35,
    wordBreak: "break-word"
  },
  relatedFields: {
    display: "grid",
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(1)
  },
  relatedField: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(1)
  },
  relatedLabel: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    fontWeight: 600,
    textTransform: "uppercase",
    whiteSpace: "nowrap"
  },
  importForm: {
    display: "grid",
    gap: theme.spacing(1.5)
  },
  fileHint: {
    color: theme.palette.text.secondary,
    fontSize: 13,
    lineHeight: 1.35,
    wordBreak: "break-word"
  }
}));

const isExpired = expiresAt => {
  if (!expiresAt) return false;
  return new Date(expiresAt).getTime() < Date.now();
};

const formatExpirationBadge = expiresAt => {
  if (!expiresAt) {
    return {
      color: "default",
      label: "Sin vencimiento"
    };
  }

  const expirationDate = new Date(expiresAt);
  if (Number.isNaN(expirationDate.getTime())) {
    return {
      color: "default",
      label: "Sin vencimiento"
    };
  }

  const today = new Date();
  const startOfToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  const startOfExpiration = new Date(
    expirationDate.getFullYear(),
    expirationDate.getMonth(),
    expirationDate.getDate()
  );
  const remainingDays = Math.ceil(
    (startOfExpiration.getTime() - startOfToday.getTime()) /
      (1000 * 60 * 60 * 24)
  );
  const formattedDate = expirationDate.toLocaleDateString("es-CR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric"
  });

  if (remainingDays < 0) {
    return {
      color: "secondary",
      label: `Expirado ${formattedDate}`
    };
  }

  return {
    color: "primary",
    label: `Expira ${formattedDate} - quedan ${remainingDays} dias`
  };
};

const formatDateInput = expiresAt => {
  if (!expiresAt) return "";
  return new Date(expiresAt).toISOString().slice(0, 10);
};

const formatWhatsappStatus = status => {
  const statusLabels = {
    CONNECTED: "Conectado",
    DISCONNECTED: "Desconectado",
    OPENING: "Conectando",
    PAIRING: "Emparejando",
    TIMEOUT: "Sin respuesta",
    qrcode: "QR pendiente"
  };

  return statusLabels[status] || status || "-";
};

const formatDateTime = value => {
  if (!value) return "-";
  return new Date(value).toLocaleString();
};

const formatCreditValue = value => {
  if (value === null || value === undefined || value === "") return "-";
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "-";
  return parsed.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4
  });
};

const formatCreditInputValue = value => {
  if (value === null || value === undefined || value === "") return "";
  const parsed = Number(value);
  return Number.isFinite(parsed) ? String(parsed) : String(value);
};

const formatFreeTrialContactPhone = record => {
  if (!record) return "";
  return [record.contactPhoneCountryCode, record.contactPhoneNumber]
    .filter(Boolean)
    .join(" ")
    .trim();
};

const normalizePhoneNumber = value => String(value || "").replace(/\D/g, "");
const getEventValue = event => event?.target?.value ?? "";

const defaultMenuPermissions = () =>
  menuPermissionOptions
    .filter(option => option.value !== "reseller")
    .map(option => option.value);
const configurableMenuPermissionValues = new Set(
  menuPermissionOptions.map(option => option.value)
);
const rowsPerPageOptions = [10, 20, 30, 40, 50];
const initialFilters = {
  emailSearch: "",
  status: "",
  permission: "",
  usersMin: "",
  usersMax: "",
  connectionsMin: "",
  connectionsMax: ""
};

const normalizeMenuPermissions = value => {
  if (Array.isArray(value)) {
    return value
      .map(item => String(item))
      .filter(item => configurableMenuPermissionValues.has(item));
  }

  if (typeof value === "string" && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed
          .map(item => String(item))
          .filter(item => configurableMenuPermissionValues.has(item));
      }
    } catch {
      return defaultMenuPermissions();
    }
  }

  return defaultMenuPermissions();
};

const formatProfile = profile => {
  if (profile === "owner") return "Dueño";
  if (profile === "superadmin") return "Superadministrador";
  return profile || "-";
};

const getVisibleCompanyUsers = (company, currentUser) => {
  const companyUsers = company.users || [];
  if (currentUser?.profile === "superadmin") return companyUsers;
  return companyUsers.filter(accountUser => accountUser.profile !== "superadmin");
};

const SuperAdmin = () => {
  const classes = useStyles();
  const { user, handleImpersonate } = useContext(AuthContext);
  const isRealSuperAdmin = user?.profile === "superadmin";
  const [companies, setCompanies] = useState([]);
  const [accountSettings, setAccountSettings] = useState({});
  const [deletingCompany, setDeletingCompany] = useState(null);
  const [resettingCompany, setResettingCompany] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(initialFilters);
  const [activeTab, setActiveTab] = useState("accounts");
  const [pageNumber, setPageNumber] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCompanies, setTotalCompanies] = useState(0);
  const [trialRecords, setTrialRecords] = useState([]);
  const [loadingTrialRecords, setLoadingTrialRecords] = useState(false);
  const [trialRecordModalOpen, setTrialRecordModalOpen] = useState(false);
  const [editingTrialRecord, setEditingTrialRecord] = useState(null);
  const [deletingTrialRecord, setDeletingTrialRecord] = useState(null);
  const [trialRecordForm, setTrialRecordForm] = useState({
    accountName: "",
    accountEmail: "",
    contactPhoneCountryCode: "",
    contactPhoneNumber: "",
    trialStartedAt: "",
    trialExpiresAt: "",
    linkedPhoneNumber: "",
    linkedAt: ""
  });
  const [exportingCompanyId, setExportingCompanyId] = useState(null);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importName, setImportName] = useState("");
  const [importSlug, setImportSlug] = useState("");
  const [importingAccount, setImportingAccount] = useState(false);
  const [aiCreditModalCompany, setAiCreditModalCompany] = useState(null);
  const [aiCreditUsageMode, setAiCreditUsageMode] = useState("reset");
  const [aiCreditUsageValue, setAiCreditUsageValue] = useState("");
  const [savingAiCreditUsage, setSavingAiCreditUsage] = useState(false);

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/superadmin/accounts", {
        params: {
          pageNumber,
          rowsPerPage,
          ...filters
        }
      });
      setCompanies(data.companies || []);
      setTotalCompanies(data.count || 0);
      setAccountSettings(
        (data.companies || []).reduce((acc, company) => {
          acc[company.id] = {
            expiresAt: formatDateInput(company.expiresAt),
            maxUsers: company.maxUsers ?? "",
            maxConnections: company.maxConnections ?? "",
            aiIncludedCreditLimit: formatCreditInputValue(
              company.aiIncludedCreditLimit
            ),
            forceNormalMode: company.forceNormalMode === true,
            menuPermissions: normalizeMenuPermissions(company.menuPermissions)
          };
          return acc;
        }, {})
      );
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  const fetchTrialRecords = async () => {
    if (!isRealSuperAdmin) return;
    setLoadingTrialRecords(true);
    try {
      const { data } = await api.get("/superadmin/free-trials");
      setTrialRecords(data.records || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingTrialRecords(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, [
    pageNumber,
    rowsPerPage,
    filters.emailSearch,
    filters.status,
    filters.permission,
    filters.usersMin,
    filters.usersMax,
    filters.connectionsMin,
    filters.connectionsMax
  ]);

  useEffect(() => {
    if (activeTab === "trials") fetchTrialRecords();
  }, [activeTab]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPageNumber(1);
  };

  const clearFilters = () => {
    setFilters(initialFilters);
    setPageNumber(1);
  };

  const handleEnterAccount = async targetUser => {
    try {
      await handleImpersonate(targetUser.id);
      toast.success(`Ingresando como ${targetUser.name}`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleSaveAccountSettings = async (companyId, overrides = {}) => {
    try {
      const settings = {
        ...(accountSettings[companyId] || {}),
        ...overrides
      };

      await api.put(`/superadmin/companies/${companyId}`, {
        expiresAt: settings.expiresAt || null,
        maxUsers: settings.maxUsers === "" ? null : settings.maxUsers,
        maxConnections:
          settings.maxConnections === "" ? null : settings.maxConnections,
        aiIncludedCreditLimit:
          settings.aiIncludedCreditLimit === ""
            ? null
            : settings.aiIncludedCreditLimit,
        forceNormalMode: settings.forceNormalMode === true,
        menuPermissions: normalizeMenuPermissions(settings.menuPermissions || [])
      });
      toast.success(i18n.t("superAdmin.toasts.settingsSaved"));
      fetchAccounts();
    } catch (err) {
      toastError(err);
    }
  };

  const openAiCreditUsageModal = company => {
    setAiCreditModalCompany(company);
    setAiCreditUsageMode("reset");
    setAiCreditUsageValue(String(Number(company.aiIncludedCreditUsage?.used || 0)));
  };

  const closeAiCreditUsageModal = () => {
    if (savingAiCreditUsage) return;
    setAiCreditModalCompany(null);
    setAiCreditUsageMode("reset");
    setAiCreditUsageValue("");
  };

  const handleSaveAiCreditUsage = async () => {
    if (!aiCreditModalCompany) return;

    setSavingAiCreditUsage(true);
    try {
      await api.post(
        `/superadmin/companies/${aiCreditModalCompany.id}/ai-credit-usage`,
        {
          action: aiCreditUsageMode,
          consumedCredits:
            aiCreditUsageMode === "reset" ? 0 : aiCreditUsageValue
        }
      );
      toast.success("Consumo de IA actualizado.");
      setAiCreditModalCompany(null);
      setAiCreditUsageMode("reset");
      setAiCreditUsageValue("");
      fetchAccounts();
    } catch (err) {
      toastError(err);
    } finally {
      setSavingAiCreditUsage(false);
    }
  };

  const handleToggleMenuPermission = (companyId, permission) => {
    setAccountSettings(prev => {
      const currentPermissions =
        prev[companyId]?.menuPermissions || defaultMenuPermissions();
      const nextPermissions = currentPermissions.includes(permission)
        ? currentPermissions.filter(item => item !== permission)
        : [...currentPermissions, permission];

      return {
        ...prev,
        [companyId]: {
          ...(prev[companyId] || {}),
          menuPermissions: nextPermissions
        }
      };
    });
  };

  const handleDeleteCompany = async companyId => {
    try {
      await api.delete(`/superadmin/companies/${companyId}`);
      toast.success(i18n.t("superAdmin.toasts.deleted"));
      setDeletingCompany(null);
      fetchAccounts();
    } catch (err) {
      toastError(err);
    }
  };

  const handleResetCompany = async companyId => {
    try {
      const { data } = await api.post(`/superadmin/companies/${companyId}/reset`);
      toast.success(
        i18n.t("superAdmin.toasts.reset") ||
          `Cuenta limpiada. Se conservo ${data.preservedUser?.email || "el admin principal"}.`
      );
      setResettingCompany(null);
      await fetchAccounts();
      if (companyId === user.companyId) {
        window.location.reload();
      }
    } catch (err) {
      toastError(err);
    }
  };

  const handleExportAccount = async company => {
    setExportingCompanyId(company.id);
    try {
      const { data, headers } = await api.get(
        `/superadmin/companies/${company.id}/export`,
        { responseType: "blob" }
      );
      const disposition = headers["content-disposition"] || "";
      const fileNameMatch = disposition.match(/filename="?([^"]+)"?/);
      const fileName =
        fileNameMatch?.[1] ||
        `kodelr-account-${company.slug || company.id}.zip`;
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toastError(err);
    } finally {
      setExportingCompanyId(null);
    }
  };

  const closeImportModal = () => {
    if (importingAccount) return;
    setImportModalOpen(false);
    setImportFile(null);
    setImportName("");
    setImportSlug("");
  };

  const handleImportAccount = async event => {
    event.preventDefault();

    if (!importFile) {
      toast.error("Selecciona un archivo de exportacion.");
      return;
    }

    const formData = new FormData();
    formData.append("file", importFile);
    if (importName.trim()) formData.append("name", importName.trim());
    if (importSlug.trim()) formData.append("slug", importSlug.trim());

    setImportingAccount(true);
    try {
      await api.post("/superadmin/companies/import", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Cuenta importada correctamente.");
      setImportModalOpen(false);
      setImportFile(null);
      setImportName("");
      setImportSlug("");
      fetchAccounts();
    } catch (err) {
      toastError(err);
    } finally {
      setImportingAccount(false);
    }
  };

  const handleChangeAccountSetting = (companyId, field, value) => {
    setAccountSettings(prev => ({
      ...prev,
      [companyId]: {
        ...(prev[companyId] || {}),
        [field]: value
      }
    }));
  };

  const handleChangeTrialRecordField = (field, value) => {
    setTrialRecordForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const openTrialRecordModal = record => {
    setEditingTrialRecord(record);
    setTrialRecordModalOpen(true);
    setTrialRecordForm({
      accountName: record?.accountName || "",
      accountEmail: record?.accountEmail || "",
      contactPhoneCountryCode: record?.contactPhoneCountryCode || "",
      contactPhoneNumber: record?.contactPhoneNumber || "",
      trialStartedAt: record?.trialStartedAt
        ? formatDateInput(record.trialStartedAt)
        : "",
      trialExpiresAt: record?.trialExpiresAt
        ? formatDateInput(record.trialExpiresAt)
        : "",
      linkedPhoneNumber: record?.linkedPhoneNumber || "",
      linkedAt: record?.linkedAt ? formatDateInput(record.linkedAt) : ""
    });
  };

  const closeTrialRecordModal = () => {
    setEditingTrialRecord(null);
    setTrialRecordModalOpen(false);
    setTrialRecordForm({
      accountName: "",
      accountEmail: "",
      contactPhoneCountryCode: "",
      contactPhoneNumber: "",
      trialStartedAt: "",
      trialExpiresAt: "",
      linkedPhoneNumber: "",
      linkedAt: ""
    });
  };

  const handleSaveTrialRecord = async event => {
    event.preventDefault();
    try {
      const payload = {
        ...trialRecordForm
      };

      if (editingTrialRecord) {
        await api.put(`/superadmin/free-trials/${editingTrialRecord.id}`, payload);
      } else {
        await api.post("/superadmin/free-trials", payload);
      }

      toast.success("Registro de prueba gratis guardado.");
      closeTrialRecordModal();
      fetchTrialRecords();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteTrialRecord = async recordId => {
    try {
      await api.delete(`/superadmin/free-trials/${recordId}`);
      toast.success("Registro de prueba gratis eliminado.");
      setDeletingTrialRecord(null);
      fetchTrialRecords();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingCompany
            ? `${i18n.t("superAdmin.confirmationModal.deleteTitle")} ${
                deletingCompany.name
              }`
            : i18n.t("superAdmin.confirmationModal.deleteTitle")
        }
        open={!!deletingCompany}
        onClose={() => setDeletingCompany(null)}
        onConfirm={() => handleDeleteCompany(deletingCompany.id)}
      >
        {i18n.t("superAdmin.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <ConfirmationModal
        title={
          resettingCompany
            ? `${i18n.t("superAdmin.confirmationModal.resetTitle")} ${
                resettingCompany.name
              }`
            : i18n.t("superAdmin.confirmationModal.resetTitle")
        }
        open={!!resettingCompany}
        onClose={() => setResettingCompany(null)}
        onConfirm={() => handleResetCompany(resettingCompany.id)}
      >
        {i18n.t("superAdmin.confirmationModal.resetMessage")}
      </ConfirmationModal>

      <ConfirmationModal
        title="Eliminar registro de prueba gratis"
        open={!!deletingTrialRecord}
        onClose={() => setDeletingTrialRecord(null)}
        onConfirm={() => handleDeleteTrialRecord(deletingTrialRecord.id)}
      >
        Este registro se eliminara del historial de pruebas gratis.
      </ConfirmationModal>

      <Dialog
        open={trialRecordModalOpen}
        onClose={closeTrialRecordModal}
        maxWidth="sm"
        fullWidth
      >
        <form onSubmit={handleSaveTrialRecord}>
          <DialogTitle>
            {editingTrialRecord ? "Editar prueba gratis" : "Agregar prueba gratis"}
          </DialogTitle>
          <DialogContent dividers>
            <div className={classes.trialForm}>
              <TextField
                label="Cuenta"
                variant="outlined"
                margin="dense"
                fullWidth
                value={trialRecordForm.accountName}
                onChange={event =>
                  handleChangeTrialRecordField("accountName", getEventValue(event))
                }
              />
              <TextField
                label="Correo"
                variant="outlined"
                margin="dense"
                fullWidth
                value={trialRecordForm.accountEmail}
                onChange={event =>
                  handleChangeTrialRecordField("accountEmail", getEventValue(event))
                }
              />
              <TextField
                label="Telefono de contacto"
                variant="outlined"
                margin="dense"
                fullWidth
                value={trialRecordForm.contactPhoneNumber}
                onChange={event =>
                  handleChangeTrialRecordField(
                    "contactPhoneNumber",
                    getEventValue(event)
                  )
                }
              />
              <TextField
                type="date"
                label="Inicio prueba"
                variant="outlined"
                margin="dense"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={trialRecordForm.trialStartedAt}
                onChange={event =>
                  handleChangeTrialRecordField(
                    "trialStartedAt",
                    getEventValue(event)
                  )
                }
              />
              <TextField
                type="date"
                label="Vence prueba"
                variant="outlined"
                margin="dense"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={trialRecordForm.trialExpiresAt}
                onChange={event =>
                  handleChangeTrialRecordField(
                    "trialExpiresAt",
                    getEventValue(event)
                  )
                }
              />
              <TextField
                label="Numero vinculado"
                variant="outlined"
                margin="dense"
                fullWidth
                value={trialRecordForm.linkedPhoneNumber}
                onChange={event =>
                  handleChangeTrialRecordField(
                    "linkedPhoneNumber",
                    getEventValue(event)
                  )
                }
              />
              <TextField
                type="date"
                label="Fecha de vinculacion"
                variant="outlined"
                margin="dense"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={trialRecordForm.linkedAt}
                onChange={event =>
                  handleChangeTrialRecordField("linkedAt", getEventValue(event))
                }
              />
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeTrialRecordModal}>Cancelar</Button>
            <Button type="submit" color="primary" variant="contained">
              Guardar
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {isRealSuperAdmin && (
      <Dialog
        open={importModalOpen}
        onClose={closeImportModal}
        maxWidth="sm"
        fullWidth
      >
        <form onSubmit={handleImportAccount}>
          <DialogTitle>Importar cuenta</DialogTitle>
          <DialogContent dividers>
            <div className={classes.importForm}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<PublishIcon />}
              >
                Seleccionar archivo ZIP
                <input
                  hidden
                  accept=".zip,application/zip"
                  type="file"
                  onChange={event => setImportFile(event.target.files?.[0] || null)}
                />
              </Button>
              <Typography className={classes.fileHint}>
                {importFile ? importFile.name : "Sin archivo seleccionado"}
              </Typography>
              <TextField
                label="Nombre nuevo"
                variant="outlined"
                margin="dense"
                fullWidth
                value={importName}
                onChange={event => setImportName(event.target.value)}
                helperText="Opcional. Si lo dejas vacio se usara el nombre exportado con sufijo Import."
              />
              <TextField
                label="Slug nuevo"
                variant="outlined"
                margin="dense"
                fullWidth
                value={importSlug}
                onChange={event => setImportSlug(event.target.value)}
                helperText="Opcional. Si existe, se ajustara automaticamente."
              />
              <Typography className={classes.fileHint}>
                Las conexiones de WhatsApp se importan desconectadas y los tokens publicos se regeneran.
              </Typography>
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeImportModal} disabled={importingAccount}>
              Cancelar
            </Button>
            <Button
              color="primary"
              variant="contained"
              type="submit"
              disabled={importingAccount || !importFile}
            >
              Importar
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      )}

      <MainHeader>
        <div className={classes.headerContent}>
          <Title>{i18n.t("superAdmin.title")}</Title>
          <MainHeaderButtonsWrapper className={classes.headerButtons}>
            {isRealSuperAdmin && (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<PublishIcon />}
                onClick={() => setImportModalOpen(true)}
                disabled={loading}
              >
                Importar cuenta
              </Button>
            )}
            <Button
              variant="outlined"
              color="primary"
              startIcon={<RefreshIcon />}
              onClick={activeTab === "trials" ? fetchTrialRecords : fetchAccounts}
              disabled={activeTab === "trials" ? loadingTrialRecords : loading}
            >
              {i18n.t("superAdmin.buttons.refresh")}
            </Button>
          </MainHeaderButtonsWrapper>
        </div>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <Tabs
          className={classes.tabBar}
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab value="accounts" label="Cuentas" />
          {isRealSuperAdmin && <Tab value="trials" label="Pruebas gratis" />}
        </Tabs>

        {activeTab === "accounts" && (
        <>
        <div className={classes.filters}>
          <TextField
            label="Buscar correo"
            variant="outlined"
            margin="dense"
            value={filters.emailSearch}
            onChange={event => handleFilterChange("emailSearch", event.target.value)}
          />
          <FormControl variant="outlined" margin="dense">
            <InputLabel>Estado</InputLabel>
            <Select
              value={filters.status || ALL_SUPERADMIN_STATUSES}
              onChange={event =>
                handleFilterChange(
                  "status",
                  event.target.value === ALL_SUPERADMIN_STATUSES
                    ? ""
                    : event.target.value
                )
              }
              label="Estado"
            >
              <MenuItem value={ALL_SUPERADMIN_STATUSES}>Todos</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
          {isRealSuperAdmin && (
            <FormControl variant="outlined" margin="dense">
              <InputLabel>Seccion</InputLabel>
              <Select
                value={filters.permission || ALL_SUPERADMIN_PERMISSIONS}
                onChange={event =>
                  handleFilterChange(
                    "permission",
                    event.target.value === ALL_SUPERADMIN_PERMISSIONS
                      ? ""
                      : event.target.value
                  )
                }
                label="Seccion"
              >
                <MenuItem value={ALL_SUPERADMIN_PERMISSIONS}>Todas</MenuItem>
                {menuPermissionOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            label="Usuarios min."
            type="number"
            variant="outlined"
            margin="dense"
            inputProps={{ min: 0 }}
            value={filters.usersMin}
            onChange={event => handleFilterChange("usersMin", event.target.value)}
          />
          <TextField
            label="Usuarios max."
            type="number"
            variant="outlined"
            margin="dense"
            inputProps={{ min: 0 }}
            value={filters.usersMax}
            onChange={event => handleFilterChange("usersMax", event.target.value)}
          />
          <TextField
            label="Conexiones min."
            type="number"
            variant="outlined"
            margin="dense"
            inputProps={{ min: 0 }}
            value={filters.connectionsMin}
            onChange={event => handleFilterChange("connectionsMin", event.target.value)}
          />
          <TextField
            label="Conexiones max."
            type="number"
            variant="outlined"
            margin="dense"
            inputProps={{ min: 0 }}
            value={filters.connectionsMax}
            onChange={event => handleFilterChange("connectionsMax", event.target.value)}
          />
          <Button variant="outlined" onClick={clearFilters}>
            Limpiar
          </Button>
        </div>

        {companies.map(company => (
          <div className={classes.accountCard} key={company.id}>
            <div className={classes.accountHeader}>
              <Typography variant="h6">{company.name}</Typography>
              <div className={classes.accountMeta}>
                <Chip size="small" label={`ID ${company.id}`} />
                {formatFreeTrialContactPhone(company.freeTrialRecord) && (
                  <Chip
                    size="small"
                    label={`Contacto ${formatFreeTrialContactPhone(company.freeTrialRecord)}`}
                  />
                )}
                <Chip
                  size="small"
                  label={`${company.users?.length || 0}/${company.maxUsers ?? "-"} usuarios`}
                />
                <Chip
                  size="small"
                  label={`${company.connections?.length || 0}/${company.maxConnections ?? "-"} conexiones`}
                />
                <Chip
                  size="small"
                  color={
                    company.aiIncludedCreditUsage?.limit != null &&
                    Number(company.aiIncludedCreditUsage?.used || 0) >=
                      Number(company.aiIncludedCreditUsage?.limit || 0)
                      ? "secondary"
                      : "default"
                  }
                  label={`IA ${formatCreditValue(
                    company.aiIncludedCreditUsage?.used
                  )}/${formatCreditValue(
                    company.aiIncludedCreditUsage?.limit
                  )} creditos`}
                />
                {normalizeMenuPermissions(company.menuPermissions).includes("reseller") && (
                  <Chip
                    size="small"
                    color={Number(company.resellerCredits || 0) > 0 ? "primary" : "default"}
                    label={`Reseller ${Number(company.resellerCredits || 0)} creditos`}
                  />
                )}
                <Chip size="small" label={company.status} />
                <Chip
                  size="small"
                  color={formatExpirationBadge(company.expiresAt).color}
                  label={formatExpirationBadge(company.expiresAt).label}
                />
                <Chip
                  size="small"
                  color={isExpired(company.expiresAt) ? "secondary" : "primary"}
                  label={
                    isExpired(company.expiresAt)
                      ? i18n.t("superAdmin.values.expired")
                      : i18n.t("superAdmin.values.active")
                  }
                />
              </div>
              {isRealSuperAdmin && (
              <div className={classes.accountControls}>
                <TextField
                  type="date"
                  label={i18n.t("superAdmin.form.expiresAt")}
                  variant="outlined"
                  margin="dense"
                  InputLabelProps={{ shrink: true }}
                  value={accountSettings[company.id]?.expiresAt || ""}
                  onChange={event =>
                    handleChangeAccountSetting(
                      company.id,
                      "expiresAt",
                      event.target.value
                    )
                  }
                />
                <TextField
                  type="number"
                  label={i18n.t("superAdmin.form.maxUsers")}
                  variant="outlined"
                  margin="dense"
                  inputProps={{ min: 0 }}
                  value={accountSettings[company.id]?.maxUsers ?? ""}
                  onChange={event =>
                    handleChangeAccountSetting(
                      company.id,
                      "maxUsers",
                      event.target.value
                    )
                  }
                />
                <TextField
                  type="number"
                  label={i18n.t("superAdmin.form.maxConnections")}
                  variant="outlined"
                  margin="dense"
                  inputProps={{ min: 0 }}
                  value={accountSettings[company.id]?.maxConnections ?? ""}
                  onChange={event =>
                    handleChangeAccountSetting(
                      company.id,
                      "maxConnections",
                      event.target.value
                    )
                  }
                />
                <TextField
                  type="number"
                  label="Creditos IA incluida"
                  variant="outlined"
                  margin="dense"
                  inputProps={{ min: 0, step: "0.01" }}
                  value={accountSettings[company.id]?.aiIncludedCreditLimit ?? ""}
                  onChange={event =>
                    handleChangeAccountSetting(
                      company.id,
                      "aiIncludedCreditLimit",
                      event.target.value
                    )
                  }
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={Boolean(accountSettings[company.id]?.forceNormalMode)}
                      onChange={event =>
                        handleChangeAccountSetting(
                          company.id,
                          "forceNormalMode",
                          event.target.checked
                        )
                      }
                    />
                  }
                  label="Modo Normal"
                />
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={() => handleSaveAccountSettings(company.id)}
                >
                  {i18n.t("superAdmin.buttons.saveSettings")}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  startIcon={<RefreshIcon />}
                  onClick={() => openAiCreditUsageModal(company)}
                >
                  Ajustar IA
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setAccountSettings(prev => ({
                      ...prev,
                      [company.id]: {
                        ...(prev[company.id] || {}),
                        expiresAt: "",
                        maxUsers: "",
                        maxConnections: "",
                        aiIncludedCreditLimit: ""
                      }
                    }));
                    handleSaveAccountSettings(company.id, {
                      expiresAt: "",
                      maxUsers: "",
                      maxConnections: "",
                      aiIncludedCreditLimit: ""
                    });
                  }}
                >
                  {i18n.t("superAdmin.buttons.clearLimits")}
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="secondary"
                  startIcon={<DeleteOutlineIcon />}
                  disabled={company.id === user.companyId}
                  onClick={() => setDeletingCompany(company)}
                >
                  {i18n.t("superAdmin.buttons.delete")}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={() => setResettingCompany(company)}
                >
                  {i18n.t("superAdmin.buttons.reset")}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  startIcon={<GetAppIcon />}
                  disabled={exportingCompanyId === company.id}
                  onClick={() => handleExportAccount(company)}
                >
                  Exportar
                </Button>
              </div>
              )}
              {isRealSuperAdmin && (
                <>
                  <Typography className={classes.sectionTitle} variant="subtitle2">
                    Secciones disponibles
                  </Typography>
                  <div className={classes.menuPermissions}>
                    {menuPermissionOptions.map(option => (
                      <FormControlLabel
                        key={option.value}
                        control={
                          <Checkbox
                            color="primary"
                            checked={Boolean(
                              accountSettings[company.id]?.menuPermissions?.includes(option.value)
                            )}
                            onChange={() => handleToggleMenuPermission(company.id, option.value)}
                          />
                        }
                        label={option.label}
                      />
                    ))}
                  </div>
                  <div className={classes.menuPermissionsActions}>
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      onClick={() => handleSaveAccountSettings(company.id)}
                    >
                      Guardar secciones
                    </Button>
                  </div>
                </>
              )}
            </div>

            <Typography className={classes.sectionTitle} variant="subtitle2">
              {i18n.t("superAdmin.sections.users")}
            </Typography>
            {getVisibleCompanyUsers(company, user).length ? (
              <div className={classes.relatedCards}>
                {getVisibleCompanyUsers(company, user).map(accountUser => (
                  <div className={classes.relatedCard} key={accountUser.id}>
                    <div className={classes.relatedCardHeader}>
                      <div>
                        <Typography className={classes.relatedTitle}>
                          {accountUser.name}
                        </Typography>
                        <Typography className={classes.relatedMeta}>
                          {accountUser.email}
                        </Typography>
                      </div>
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        disabled={accountUser.id === user.id}
                        onClick={() => handleEnterAccount(accountUser)}
                      >
                        {i18n.t("superAdmin.buttons.enter")}
                      </Button>
                    </div>
                    <div className={classes.relatedFields}>
                      <div className={classes.relatedField}>
                        <span className={classes.relatedLabel}>
                          {i18n.t("superAdmin.table.profile")}
                        </span>
                        <Typography variant="body2">
                          {formatProfile(accountUser.profile)}
                        </Typography>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <Typography className={classes.emptyText} variant="body2">
                {i18n.t("superAdmin.empty.users") || "No hay usuarios."}
              </Typography>
            )}

            <Typography className={classes.sectionTitle} variant="subtitle2">
              {i18n.t("superAdmin.sections.connections")}
            </Typography>
            {company.connections?.length ? (
              <div className={classes.relatedCards}>
                {company.connections.map(whatsapp => (
                  <div className={classes.relatedCard} key={whatsapp.id}>
                    <Typography className={classes.relatedTitle}>
                      {whatsapp.name}
                    </Typography>
                    <Typography className={classes.relatedMeta}>
                      {whatsapp.number || "-"}
                    </Typography>
                    <div className={classes.relatedFields}>
                      <div className={classes.relatedField}>
                        <span className={classes.relatedLabel}>
                          {i18n.t("superAdmin.table.status")}
                        </span>
                        <Typography variant="body2">
                          {formatWhatsappStatus(whatsapp.status)}
                        </Typography>
                      </div>
                      <div className={classes.relatedField}>
                        <span className={classes.relatedLabel}>
                          {i18n.t("superAdmin.table.default")}
                        </span>
                        <Typography variant="body2">
                          {whatsapp.isDefault
                            ? i18n.t("superAdmin.values.yes")
                            : i18n.t("superAdmin.values.no")}
                        </Typography>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <Typography className={classes.emptyText} variant="body2">
                {i18n.t("superAdmin.empty.connections")}
              </Typography>
            )}
          </div>
        ))}

        {!companies.length && !loading && (
          <Typography className={classes.emptyText} variant="body2">
            {i18n.t("superAdmin.empty.accounts")}
          </Typography>
        )}

        <TablePagination
          className={classes.pagination}
          component="div"
          count={totalCompanies}
          page={pageNumber - 1}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={rowsPerPageOptions}
          labelRowsPerPage="Filas"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `mas de ${to}`}`
          }
          onChangePage={(_, nextPage) => setPageNumber(nextPage + 1)}
          onChangeRowsPerPage={event => {
            setRowsPerPage(Number(event.target.value));
            setPageNumber(1);
          }}
        />
        </>
        )}

        {activeTab === "trials" && isRealSuperAdmin && (
          <>
            <div className={classes.menuPermissionsActions}>
              <Button
                color="primary"
                variant="contained"
                onClick={() => openTrialRecordModal(null)}
              >
                Agregar registro
              </Button>
            </div>
            <div className={classes.tableWrapper}>
              <Table size="small" className={classes.table}>
                <TableHead>
                  <TableRow>
                    <TableCell>Cuenta</TableCell>
                    <TableCell>Correo</TableCell>
                    <TableCell>Contacto</TableCell>
                    <TableCell>Inicio</TableCell>
                    <TableCell>Vence</TableCell>
                    <TableCell>Numero vinculado</TableCell>
                    <TableCell>Origen</TableCell>
                    <TableCell align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {trialRecords.map(record => (
                    <TableRow key={record.id}>
                      <TableCell>{record.accountName || "-"}</TableCell>
                      <TableCell>{record.accountEmail || "-"}</TableCell>
                      <TableCell>
                        {[
                          record.contactPhoneCountryCode,
                          record.contactPhoneNumber
                        ]
                          .filter(Boolean)
                          .join(" ") || "-"}
                      </TableCell>
                      <TableCell>{formatDateTime(record.trialStartedAt)}</TableCell>
                      <TableCell>{formatDateTime(record.trialExpiresAt)}</TableCell>
                      <TableCell>{record.linkedPhoneNumber || "-"}</TableCell>
                      <TableCell>
                        {record.source === "manual" ? "Manual" : "Registro"}
                      </TableCell>
                      <TableCell align="right" className={classes.actionCell}>
                        <IconButton
                          size="small"
                          title="Editar"
                          onClick={() => openTrialRecordModal(record)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          title="Eliminar"
                          onClick={() => setDeletingTrialRecord(record)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!trialRecords.length && !loadingTrialRecords && (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        No hay pruebas gratis registradas.
                      </TableCell>
                    </TableRow>
                  )}
                  {loadingTrialRecords && (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        Cargando...
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </Paper>

      <Dialog
        open={Boolean(aiCreditModalCompany)}
        onClose={closeAiCreditUsageModal}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Ajustar consumo IA</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary">
            {aiCreditModalCompany?.name || ""}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Consumo actual:{" "}
            {formatCreditValue(aiCreditModalCompany?.aiIncludedCreditUsage?.used)}{" "}
            creditos
          </Typography>
          <FormControl variant="outlined" margin="dense" fullWidth>
            <InputLabel>Accion</InputLabel>
            <Select
              value={aiCreditUsageMode}
              onChange={event => setAiCreditUsageMode(event.target.value)}
              label="Accion"
            >
              <MenuItem value="reset">Restablecer a 0</MenuItem>
              <MenuItem value="set">Establecer consumo manual</MenuItem>
            </Select>
          </FormControl>
          {aiCreditUsageMode === "set" && (
            <TextField
              autoFocus
              fullWidth
              type="number"
              label="Consumo en creditos"
              variant="outlined"
              margin="dense"
              inputProps={{ min: 0, step: "0.0001" }}
              value={aiCreditUsageValue}
              onChange={event => setAiCreditUsageValue(event.target.value)}
            />
          )}
          <Typography variant="caption" color="textSecondary">
            Se actualizara directamente el valor consumido acumulado de la cuenta.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAiCreditUsageModal} disabled={savingAiCreditUsage}>
            Cancelar
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleSaveAiCreditUsage}
            disabled={savingAiCreditUsage}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default SuperAdmin;

